// `window.gtag` / `window.dataLayer` are declared in src/global.d.ts.
import type { AnyRouter } from '@tanstack/react-router'
import { CONSENT_CHANGE_EVENT, readConsent } from './cookie-consent'

export const gtag = (...args: unknown[]): void => {
  if (typeof window !== 'undefined') {
    if (typeof window.gtag === 'undefined') {
      window.dataLayer = window.dataLayer ?? []
      window.gtag = function () {
        window.dataLayer?.push(arguments)
      }
    }

    window.gtag(...args)
  }
}

// GA is a "statistics" cookie: injected only on consent, and Consent Mode stops collection if consent is later withdrawn.
let gaLoaded = false
let pageViewsBound = false

function loadGoogleAnalytics(measurementId: string): void {
  if (gaLoaded || typeof document === 'undefined') return
  gaLoaded = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  gtag('js', new Date())
  // Before `config`, or the first automatic page_view is collected before consent is granted.
  gtag('consent', 'default', { analytics_storage: 'denied' })
  gtag('config', measurementId)
}

function syncAnalyticsConsent(granted: boolean): void {
  gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' })
}

function bindPageViews(router: AnyRouter): void {
  if (pageViewsBound) return
  pageViewsBound = true

  router.history.subscribe((update: { location: { href: string } }) => {
    // Re-check on every navigation so consent withdrawn after load stops sending events.
    if (readConsent().statistics) {
      gtag('event', 'page_view', { page_location: update.location.href })
    }
  })
}

export function setupAnalytics(router: AnyRouter): void {
  if (typeof window === 'undefined' || !import.meta.env.PROD) return

  // Falls back to the historical hardcoded ID when GA_MEASUREMENT_ID is unset.
  const measurementId = window.GA_MEASUREMENT_ID || 'G-8S9DQNJQP9'

  const apply = (): void => {
    const granted = readConsent().statistics
    if (granted) {
      loadGoogleAnalytics(measurementId)
      bindPageViews(router)
    }
    // A withdrawal after load must flip analytics_storage back to denied; no-op before GA has ever loaded.
    if (gaLoaded) syncAnalyticsConsent(granted)
  }

  apply()
  window.addEventListener(CONSENT_CHANGE_EVENT, apply)
}
